import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { environment } from 'src/environments/environments';
import { lastValueFrom, Observable } from 'rxjs';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { ErrorOverlayService } from 'src/app/shared/services/overlay.service';

@Injectable({
  providedIn: 'root',
})
export class KeycloakInitService {
  private refreshTimeout: any;

  constructor(
    private keycloak: KeycloakService,
    private http: HttpClient,
    private snackbarService: SnackbarService,
    private overlayService: ErrorOverlayService,
  ) {}

  initialize(): Promise<void> {
    return this.keycloak
      .init({
        config: {
          url: environment.keycloakUrl,
          realm: environment.keycloakRealm,
          clientId: environment.clientId,
        },
        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false,
        },
        enableBearerInterceptor: true,
        bearerPrefix: 'Bearer',
        bearerExcludedUrls: [
          'api.postalpincode.in',
          '/sign-up',
          '/terms-and-conditions',
          'vision-erp-uat-data.s3.amazonaws.com/sample_template/',
          'vision-erp-data-prod-data.s3.amazonaws.com/sample_template/',
          'vision-erp-data-uat-data.s3.amazonaws.com/sample_template/',
          'vision-erp-data-prod-data.s3.amazonaws.com/sample_template/',
          'vision-erp-data.s3.amazonaws.com/sample_template/',
          'vision-erp.s3.amazonaws.com/sample_template/',

          'vision-erp-prod-data.s3.amazonaws.com/sample_template/',
        ],
      })
      .then(() => {
        const keycloakInstance = this.keycloak.getKeycloakInstance();
        this.setupTokenRefresh(keycloakInstance);

        const roleId = keycloakInstance.tokenParsed?.['role_id'];

        if (roleId && roleId !== 'SA') {
          return lastValueFrom(
            this.http.get(`${environment.dataApiUrl}api/roles/${roleId}`),
          )
            .then((response: any) => {
              sessionStorage.setItem(
                'userPermissions',
                JSON.stringify(response.data),
              );
            })
            .catch((error) => {
              this.snackbarService.openSnackBar({
                message: 'Error fetching user id',
                type: 'error',
              });
            });
        } else {
          console.warn('roleId is not defined');
          return Promise.resolve();
        }
      })
      .then(() => {
        const keycloakInstance = this.keycloak.getKeycloakInstance();
        const companyId = keycloakInstance.tokenParsed?.['company_id'];
        const roleId = keycloakInstance.tokenParsed?.['role_id'];

        if (roleId && roleId !== 'SA' && companyId) {
          return lastValueFrom(
            this.http.get(
              `${environment.dataApiUrl}api/companies/${companyId}`,
            ),
          )
            .then((response: any) => {
              // Check if company is active
              if (response.data && response.data.is_active === false) {
                this.overlayService.showCompanyOverlay(
                  'Your organization has been disabled. Please contact your administrator for assistance.',
                  'Organization Disabled'
                );
                return;
              }
              
              sessionStorage.setItem(
                'companyDetails',
                JSON.stringify(response.data),
              );
            })
            .catch((error) => {
              this.snackbarService.openSnackBar({
                message: 'Error fetching user id',
                type: 'error',
              });
            });
        } else {
          console.warn('companyId is not defined');
          return Promise.resolve();
        }
      });
  }

  /**
   * Sets up dynamic token refresh based on the token's expiration time
   */
  private setupTokenRefresh(keycloakInstance: any): void {
    // Clear any existing timeout
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    // Register for auth events
    this.keycloak.keycloakEvents$.subscribe((event) => {
      if (event.type === KeycloakEventType.OnTokenExpired) {
        this.refreshToken(keycloakInstance);
      } else if (
        event.type === KeycloakEventType.OnAuthSuccess ||
        event.type === KeycloakEventType.OnAuthRefreshSuccess
      ) {
        // When authentication succeeds or token is refreshed, schedule the next refresh
        this.scheduleNextRefresh(keycloakInstance);
      }
    });

    // Initial token check and schedule
    this.scheduleNextRefresh(keycloakInstance);
  }

  /**
   * Schedules the next token refresh based on the token's expiration time
   */
  private scheduleNextRefresh(keycloakInstance: any): void {
    // Clear any existing timeout
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    const expiresIn = this.getTokenExpirationTime(keycloakInstance);
    if (expiresIn <= 0) {
      // Token is already expired, refresh immediately
      this.refreshToken(keycloakInstance);
      return;
    }

    // Schedule refresh for 70% of the token's lifetime
    // This gives us a buffer before expiration but doesn't refresh too early
    const refreshIn = Math.max(expiresIn * 0.7, 1); // At least 1ms, max 70% of expiry time

    this.refreshTimeout = setTimeout(() => {
      this.refreshToken(keycloakInstance);
    }, refreshIn * 1000); // Convert to milliseconds
  }

  /**
   * Gets the number of seconds until the token expires
   */
  private getTokenExpirationTime(keycloakInstance: any): number {
    if (
      !keycloakInstance ||
      !keycloakInstance.tokenParsed ||
      !keycloakInstance.tokenParsed.exp
    ) {
      return 0;
    }

    const expiresAt = keycloakInstance.tokenParsed.exp * 1000; // Convert to milliseconds
    const now = new Date().getTime();
    return Math.max(0, Math.floor((expiresAt - now) / 1000)); // Return seconds remaining
  }

  /**
   * Refreshes the token
   */
  private refreshToken(keycloakInstance: any): void {
    keycloakInstance
      .updateToken(30) // Minimum validity: 30 seconds
      .then((refreshed: boolean) => {
        if (refreshed) {
          const tokenExpiryTime = keycloakInstance.tokenParsed?.exp
            ? new Date(keycloakInstance.tokenParsed.exp * 1000)
            : 'unknown';

          // Token refreshed successfully, schedule the next refresh
          this.scheduleNextRefresh(keycloakInstance);
        } else {
          // Re-check our scheduling since the token is still valid
          this.scheduleNextRefresh(keycloakInstance);
        }
      })
      .catch((error: any) => {
        console.error('Failed to refresh token', error);

        // Check if we're on the sign-up page before redirecting
        if (
          !window.location.pathname.includes('/sign-up') &&
          !window.location.pathname.includes('/client/service-request') &&
          !window.location.pathname.includes('/terms-and-conditions')
        ) {
          // Handle session expiry
          this.snackbarService.openSnackBar({
            message: 'Your session has expired. Please log in again.',
            type: 'error',
          });

          // Redirect to login only if not on sign-up page or static client side service request page
          keycloakInstance.login();
        }
      });
  }

  /**
   * Cleanup method that should be called when the service is destroyed
   */
  public destroy(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
  }
}
